import { createApp } from 'vue';
import './style.css';
import App from './App.vue';
import { DynamicDialogPlugin } from './components/ui/dynamic-dialog';
import i18next from 'i18next';
import I18NextVue from 'i18next-vue';

import nl from './locales/nl/translation.json';
import zodNl from './locales/nl/zod.json';
import countriesNl from './locales/nl/countries.json';

import en from './locales/en/translation.json';
import zodEn from './locales/en/zod.json';
import countriesEn from './locales/en/countries.json';

import de from './locales/de/translation.json';
import zodDe from './locales/de/zod.json';
import countriesDe from './locales/de/countries.json';

import dk from './locales/dk/translation.json';
import zodDk from './locales/dk/zod.json';
import countriesDk from './locales/dk/countries.json';

import fr from './locales/fr/translation.json';
import zodFr from './locales/fr/zod.json';
import countriesFr from './locales/fr/countries.json';

import { makeZodI18nMap } from 'zod-i18n-map';
import { getSavedLanguage } from '@/utils/langUtils.ts';
import { createRouter, createWebHistory, NavigationGuard } from 'vue-router';
import { VueQueryPlugin } from '@tanstack/vue-query';
import { z } from 'zod';
import { configure } from 'vee-validate';
import { createPinia } from 'pinia';
import { useAuthStore } from '@/features/auth/composables/useAuthStore.ts';
import * as Sentry from '@sentry/vue';
import { createGtag } from 'vue-gtag';

const app = createApp(App);

// Add dynamic dialog plugin
app.use(DynamicDialogPlugin);

// Add i18n plugin
i18next.init({
  lng: getSavedLanguage(),
  resources: {
    nl: {
      translation: nl,
      zod: zodNl,
      countries: countriesNl,
    },
    en: {
      translation: en,
      zod: zodEn,
      countries: countriesEn,
    },
    de: {
      translation: de,
      zod: zodDe,
      countries: countriesDe,
    },
    dk: {
      translation: dk,
      zod: zodDk,
      countries: countriesDk,
    },
    fr: {
      translation: fr,
      zod: zodFr,
      countries: countriesFr,
    },
  },
});
app.use(I18NextVue, { i18next });
app.use(VueQueryPlugin);
z.setErrorMap(makeZodI18nMap());

configure({
  validateOnBlur: false, // controls if `blur` events should trigger validation with `handleChange` handler
  validateOnChange: true, // controls if `change` events should trigger validation with `handleChange` handler
  validateOnInput: false, // controls if `input` events should trigger validation with `handleChange` handler
  validateOnModelUpdate: true, // controls if `update:modelValue` events should trigger validation with `handleChange` handler
});

const authMiddleware: NavigationGuard = (to, _from, next) => {
  const authRequired = to.meta.authRequired;
  if (!authRequired) {
    next();
    return;
  }

  const authStore = useAuthStore();
  if (authStore.isAuthenticated) {
    next();
  } else {
    next({
      path: '/login',
      query: { redirect: to.fullPath },
    });
  }
};

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      redirect: () => ({
        path: '/cart',
      }),
    },
    {
      path: '/_internal/display',
      component: () => import('@/pages/DisplayConfigurationSetPage.vue'),
    },
    {
      path: '/login',
      component: () => import('@/pages/LoginPage.vue'),
    },
    {
      path: '/reset-password',
      component: () => import('@/pages/ResetPasswordPage.vue'),
    },
    {
      path: '/my-designs',
      component: () => import('@/pages/MyDesignsPage.vue'),
      beforeEnter: authMiddleware,
      meta: {
        authRequired: true,
      },
    },
    {
      path: '/my-orders',
      component: () => import('@/pages/MyOrdersPage.vue'),
      beforeEnter: authMiddleware,
      meta: {
        authRequired: true,
      },
    },
    {
      path: '/my-orders/:orderUuid',
      component: () => import('@/pages/MyOrderDetailPage.vue'),
      beforeEnter: authMiddleware,
      meta: {
        authRequired: true,
      },
    },
    {
      path: '/cart',
      component: () => import('@/pages/CartPage.vue'),
      beforeEnter: authMiddleware,
    },
    {
      path: '/checkout',
      component: () => import('@/pages/CheckoutPage.vue'),
      beforeEnter: authMiddleware,
      meta: {
        authRequired: true,
      },
    },
    {
      path: '/checkout/complete',
      component: () => import('@/pages/CheckoutCompletePage.vue'),
      beforeEnter: authMiddleware,
      meta: {
        authRequired: true,
      },
    },
    {
      path: `/set/:configuratorSetUuid`,
      component: () => import('@/pages/ConfiguratorSetPage.vue'),
    },
    {
      path: `/configured-set/:configuredSetUuid/:hash?`,
      component: () => import('@/pages/ConfiguredSetPage.vue'),
    },
    {
      path: `/configured-set/:configuredSetUuid/shared/:hash`,
      component: () => import('@/pages/ConfiguredSetViewPage.vue'),
    },
    // 404 page
    {
      path: '/:pathMatch(.*)*',
      component: () => import('@/pages/NotFoundPage.vue'),
    },
  ],
});
router.beforeEach(authMiddleware);

app.use(router);

const pinia = createPinia();
app.use(pinia);

if (window.GOOGLE_ANALYTICS_PROPERTY_ID) {
  app.use(
    createGtag({
      tagId: window.GOOGLE_ANALYTICS_PROPERTY_ID,
      pageTracker: {
        router,
      },
    }),
  );
}

Sentry.init({
  app,
  dsn: 'https://589d895e2faee75ef848d04bf01b1979@o139452.ingest.us.sentry.io/4508931320512512',
  integrations: [Sentry.browserTracingIntegration({ router })],
  tracesSampleRate: 1.0, // Capture 100% of the transactions
  tracePropagationTargets: [
    'localhost',
    'https://admin-configurator.jopamx.com/',
    'https://jopa-admin.dev.rohaanit.nl/',
    'http://jopa-portal.test/',
  ],
});

app.mount('#app');
